/**
 * A REGRA de ler o cronograma COLADO da tabela do Word (000272).
 *
 * ---------------------------------------------------------------------------
 * Por que colar, e não importar o arquivo
 * ---------------------------------------------------------------------------
 * O cronograma de cada produto é um `.docx` no Drive. Escrever um leitor de
 * `.docx` que preserve TABELA é trabalho de verdade — o extrator que esta casa
 * tem perde a tabela —, e o hub não tem credencial do Google para buscar o
 * arquivo. Colar a tabela é o caminho que já funciona hoje: o operador abre o
 * documento, seleciona a tabela, copia e cola. O navegador entrega as células
 * separadas por TAB.
 *
 * Isso é módulo PURO, com prova própria, pela regra da casa: a conta que
 * decide o que vai para o banco não mora dentro de um `.tsx`.
 *
 * ---------------------------------------------------------------------------
 * O que a tabela É — medido no cronograma real da Pós Perícia (21/09/2026)
 * ---------------------------------------------------------------------------
 *   Nº · Prazo (dias) · Data de liberação · Módulo · Disciplina
 *   1  · 0 (já liberada) · 20/07/2026 · Módulo 1 · Disciplina I – O que é Perícia
 *   2  · 5              · 25/07/2026 · Módulo 1 · Disciplina II – O que é ser Perito
 *
 * Três coisas que a medição decidiu:
 *
 *   · **A DATA da tabela é ignorada.** Ela é `início + prazo` nas 25 de 25
 *     linhas, ou seja é derivada — e guardar o que se pode derivar é a cicatriz
 *     do teto do MEI. O que entra é o PRAZO; a data volta do banco.
 *     Mas ela não é jogada fora em silêncio: `datasQueNaoConferem` compara a
 *     data escrita com a calculada e devolve as que discordam, porque uma
 *     divergência ali quer dizer que o documento foi editado à mão.
 *
 *   · **O prazo vem com texto junto** ("0 (já liberada)"). Sai o número.
 *
 *   · **O nome tem prefixo de posição** ("Disciplina III – Perícia Tributária"),
 *     e o nome na grade é só o que vem DEPOIS do travessão. O prefixo é
 *     numeração dentro do módulo, e numeração se deriva da posição — nunca se
 *     guarda. O travessão pode ser `–`, `—` ou `-`: os três aparecem em
 *     documentos da casa, e o en dash é o do Google Docs.
 */

/** Uma linha da tabela colada, já interpretada. */
export type LinhaColada = {
  /** O nome como veio, sem o prefixo "Disciplina N –". */
  nome: string;
  prazo_dias: number | null;
  modulo: number | null;
  /** A data que o documento ESCREVE. Só serve para conferir — não vai ao banco. */
  data_escrita: string | null;
  /** A linha crua, para a tela poder mostrar o que não entendeu. */
  cru: string;
};

/** Sem acento, sem caixa, sem espaço dobrado — para casar nome com a grade. */
export function chaveDeNome(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Tira o prefixo de posição do nome.
 *
 * "Disciplina III – Perícia Tributária" → "Perícia Tributária"
 * "Perícia Tributária"                  → "Perícia Tributária"
 *
 * O corte só acontece quando o que vem ANTES do travessão é mesmo um prefixo de
 * posição. Cortar no primeiro travessão qualquer estragaria um nome legítimo
 * como "Contabilidade – teoria e prática", e nomes com travessão existem.
 */
export function semPrefixoDePosicao(nome: string): string {
  const m = nome.match(/^\s*(disciplina|unidade|m[oó]dulo|aula)\s+[IVXLCDM0-9]+\s*[–—-]\s*(.+)$/i);
  return (m ? m[2] : nome).trim();
}

/** O primeiro número inteiro do texto. "0 (já liberada)" → 0; "" → null. */
function primeiroNumero(s: string): number | null {
  const m = s.match(/-?\d+/);
  if (!m) return null;
  const n = Number(m[0]);
  return Number.isFinite(n) ? n : null;
}

/** `20/07/2026` → `2026-07-20`. Devolve null no que não for data. */
export function dataBrParaIso(s: string): string | null {
  const m = s.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const [, d, mes, a] = m;
  const iso = `${a}-${mes.padStart(2, "0")}-${d.padStart(2, "0")}`;
  // Uma data como 31/02 casa o formato e não existe. O round-trip pega.
  const dt = new Date(`${iso}T12:00:00Z`);
  return Number.isNaN(dt.getTime()) || dt.toISOString().slice(0, 10) !== iso ? null : iso;
}

/**
 * Lê a tabela colada.
 *
 * Aceita TAB (o que o navegador entrega ao colar de uma tabela) e também duas
 * ou mais ESPAÇOS, porque colar de um PDF às vezes não traz TAB nenhum.
 *
 * As colunas são achadas pelo CONTEÚDO, não pela posição: a célula que é só um
 * número pequeno é o prazo, a que casa `dd/mm/aaaa` é a data, a que começa com
 * "Módulo" é o módulo, e o que sobra é o nome. Cravar a posição quebraria no
 * primeiro documento que trouxesse as colunas em outra ordem — e cada produto
 * tem o seu Word.
 */
export function lerCronogramaColado(texto: string): LinhaColada[] {
  const linhas: LinhaColada[] = [];

  for (const cru of texto.split(/\r?\n/)) {
    if (!cru.trim()) continue;

    const celulas = cru
      .split(/\t+| {2,}|\s*\|\s*/)
      .map((c) => c.trim())
      .filter((c) => c !== "");
    if (celulas.length < 2) continue;

    // O cabeçalho. Ele é reconhecido pelo conjunto de rótulos, e não pela
    // posição: um documento pode não ter cabeçalho nenhum.
    const junto = chaveDeNome(celulas.join(" "));
    if (
      junto.includes("disciplina") &&
      (junto.includes("prazo") || junto.includes("data de liberacao"))
    ) {
      continue;
    }

    let prazo: number | null = null;
    let modulo: number | null = null;
    let data: string | null = null;
    const sobra: string[] = [];

    for (const c of celulas) {
      const k = chaveDeNome(c);

      if (data === null && dataBrParaIso(c) !== null) {
        data = dataBrParaIso(c);
        continue;
      }
      if (modulo === null && /^modulo\b/.test(k)) {
        modulo = primeiroNumero(c);
        continue;
      }
      // Célula que é SÓ número (ou número com um comentário curto entre
      // parênteses) é candidata a prazo — mas a PRIMEIRA delas costuma ser o
      // "Nº" da linha, que não interessa. Por isso as duas primeiras entram
      // numa fila e quem decide é a ordem: nº vem antes de prazo.
      if (/^-?\d+\s*(\(.*\))?$/.test(c)) {
        sobra.push(`\u0000num:${c}`);
        continue;
      }
      sobra.push(c);
    }

    const numeros = sobra.filter((s) => s.startsWith("\u0000num:"));
    const textos = sobra.filter((s) => !s.startsWith("\u0000num:"));

    // Com dois números, o primeiro é o "Nº" e o segundo é o prazo. Com um só,
    // ele é o prazo — é o caso do documento sem a coluna de numeração.
    if (numeros.length >= 2) prazo = primeiroNumero(numeros[1].slice(5));
    else if (numeros.length === 1) prazo = primeiroNumero(numeros[0].slice(5));

    // O nome é o texto mais longo que sobrou: numa tabela de cinco colunas, o
    // que resta além de nº, prazo, data e módulo é a disciplina.
    const nomeCru = textos.sort((a, b) => b.length - a.length)[0] ?? "";
    const nome = semPrefixoDePosicao(nomeCru);
    if (!nome) continue;

    linhas.push({ nome, prazo_dias: prazo, modulo, data_escrita: data, cru });
  }

  return linhas;
}

/**
 * Casa as linhas coladas com as disciplinas da grade, por NOME sem acento.
 *
 * Devolve os três desfechos separados, porque cada um tem uma conduta
 * diferente e juntá-los esconderia justamente o que precisa de gente:
 *   · `casaram`   — vai para o banco;
 *   · `sem_grade` — está no documento e não na grade (ponha na grade antes);
 *   · `sem_documento` — está na grade e não no documento (fica sem prazo).
 */
export function casarComAGrade(
  coladas: LinhaColada[],
  grade: { disciplina_id: string; nome: string }[],
): {
  casaram: { disciplina_id: string; nome: string; prazo_dias: number | null; modulo: number | null }[];
  sem_grade: LinhaColada[];
  sem_documento: { disciplina_id: string; nome: string }[];
} {
  const porChave = new Map(grade.map((d) => [chaveDeNome(d.nome), d]));
  const casaram: ReturnType<typeof casarComAGrade>["casaram"] = [];
  const sem_grade: LinhaColada[] = [];
  const usados = new Set<string>();

  for (const l of coladas) {
    const d = porChave.get(chaveDeNome(l.nome));
    if (!d || usados.has(d.disciplina_id)) {
      sem_grade.push(l);
      continue;
    }
    usados.add(d.disciplina_id);
    casaram.push({
      disciplina_id: d.disciplina_id,
      nome: d.nome,
      prazo_dias: l.prazo_dias,
      modulo: l.modulo,
    });
  }

  return {
    casaram,
    sem_grade,
    sem_documento: grade.filter((d) => !usados.has(d.disciplina_id)),
  };
}

/**
 * As datas que o documento escreve e que NÃO batem com `início + prazo`.
 *
 * O cronograma da Perícia fecha nas 25 de 25 — e é justamente por isso que a
 * data não é guardada. Quando alguma não fecha, o documento foi editado à mão e
 * alguém precisa olhar: aceitar em silêncio seria escolher um dos dois sem
 * dizer qual.
 */
export function datasQueNaoConferem(
  inicio: string | null,
  linhas: LinhaColada[],
): { nome: string; escrita: string; calculada: string }[] {
  if (!inicio) return [];
  const fora: { nome: string; escrita: string; calculada: string }[] = [];

  for (const l of linhas) {
    if (l.data_escrita === null || l.prazo_dias === null) continue;
    // Meio-dia em UTC, e não meia-noite: em -03:00 a meia-noite cai no dia
    // anterior, que é a cicatriz do fato gerador das Calculadoras.
    const base = new Date(`${inicio}T12:00:00Z`);
    base.setUTCDate(base.getUTCDate() + l.prazo_dias);
    const calculada = base.toISOString().slice(0, 10);
    if (calculada !== l.data_escrita) {
      fora.push({ nome: l.nome, escrita: l.data_escrita, calculada });
    }
  }
  return fora;
}
